import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, User, ArrowLeft, Clock, Share2, TrendingUp, Tag, Eye } from "lucide-react";
// Social Media Icons
import { FaFacebookF, FaTwitter, FaLinkedinIn, FaWhatsapp } from "react-icons/fa";

const BlogDetail = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [allBlogs, setAllBlogs] = useState([]); // Sidebar data ke liye
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Jab bhi naya blog khule, page ko upar scroll kar de
    window.scrollTo(0, 0);

    const fetchData = async () => {
      setLoading(true);
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'https://crm.cashpro.co.in';

        // Dono APIs ko ek sath call kar rahe hain (Single Blog + All Blogs for Sidebar)
        const [singleRes, allRes] = await Promise.all([
          fetch(`${apiUrl}/api/blog/${id}`),
          fetch(`${apiUrl}/api/blogs`)
        ]);

        const singleResult = await singleRes.json();
        const allResult = await allRes.json();

        if (singleResult.success) {
          setPost(singleResult.data);
        } else {
          setError("Blog post not found.");
        }

        if (allResult.success) {
          setAllBlogs(allResult.data);
        }
      } catch (err) {
        console.error("Error fetching blog details:", err);
        setError("Failed to load blog details.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]); // ID change hone par wapas fetch hoga

  // YouTube URL Converter
  const getYoutubeEmbedUrl = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? `https://www.youtube.com/embed/${match[2]}` : null;
  };

  // --- SIDEBAR LOGIC ---
  const categories = ["All", ...new Set(allBlogs.map(p => p.category_name || "Uncategorized"))];
  const getCategoryCount = (category) => {
    if (category === "All") return allBlogs.length;
    return allBlogs.filter(p => (p.category_name || "Uncategorized") === category).length;
  };

  // Popular Posts (Abhi ke liye top 4 latest utha rahe hain)
  const popularPosts = [...allBlogs].slice(0, 4);

  // --- SOCIAL SHARE URLS ---
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = post ? encodeURIComponent(post.title) : '';

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-gray-50">
        <h2 className="text-2xl text-red-500 font-bold mb-4">{error || "Post not found"}</h2>
        <Link to="/blogs" className="text-green-600 hover:underline">Go back to all blogs</Link>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen py-8 md:py-12">
      <div className="max-w-7xl mx-auto px-4">
        
        {/* Back Button */}
        <Link to="/blogs" className="inline-flex items-center text-green-600 hover:text-green-800 font-medium mb-6 transition-colors">
          <ArrowLeft size={18} className="mr-2" /> Back to all articles
        </Link>

        <div className="flex flex-col lg:flex-row gap-8">
          
          {/* ======================================= */}
          {/* LEFT SIDE: MAIN BLOG CONTENT (65% width)*/}
          {/* ======================================= */}
          <div className="lg:w-2/3">
            
            {/* Blog Header */}
            <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 mb-6">
              <div className="mb-4">
                <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
                  {post.category_name || "Uncategorized"}
                </span>
              </div>
              
              <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6 leading-tight">
                {post.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-gray-500 text-sm border-t border-gray-100 pt-6">
                <span className="flex items-center gap-1.5"><Calendar size={16} /> {new Date(post.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                <span className="flex items-center gap-1.5"><User size={16} /> {post.author_id === 'admin' ? 'CashPro Admin' : 'Financial Expert'}</span>
                <span className="flex items-center gap-1.5"><Clock size={16} /> 5 min read</span>
              </div>
            </div>

            {/* Blog Thumbnail */}
            {post.thumbnail_url && (
              <div className="rounded-2xl overflow-hidden shadow-md mb-6">
                <img src={post.thumbnail_url} alt={post.title} className="w-full h-auto object-cover max-h-[500px]" />
              </div>
            )}

            {/* Optional YouTube Video */}
            {post.youtube_link && getYoutubeEmbedUrl(post.youtube_link) && (
              <div className="rounded-2xl overflow-hidden shadow-md mb-6 relative" style={{ paddingTop: '56.25%' }}>
                <iframe 
                  className="absolute top-0 left-0 w-full h-full"
                  src={getYoutubeEmbedUrl(post.youtube_link)} 
                  title="YouTube video player" 
                  frameBorder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                  allowFullScreen>
                </iframe>
              </div>
            )}

            {/* Blog HTML Content */}
            <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 mb-6">
              <div 
                className="prose prose-lg prose-green max-w-none text-gray-700"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />

              {/* --- SOCIAL MEDIA SHARE BUTTONS --- */}
              <div className="mt-10 pt-6 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="font-bold text-gray-700 flex items-center gap-2">
                  <Share2 size={20} className="text-green-600"/> Share this post:
                </div>
                <div className="flex items-center gap-3">
                  {/* WhatsApp */}
                  <a href={`https://api.whatsapp.com/send?text=${shareTitle} ${currentUrl}`} target="_blank" rel="noreferrer" 
                     className="w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center hover:scale-110 transition shadow-sm">
                    <FaWhatsapp size={18} />
                  </a>
                  {/* Facebook */}
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${currentUrl}`} target="_blank" rel="noreferrer" 
                     className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:scale-110 transition shadow-sm">
                    <FaFacebookF size={18} />
                  </a>
                  {/* Twitter / X */}
                  <a href={`https://twitter.com/intent/tweet?url=${currentUrl}&text=${shareTitle}`} target="_blank" rel="noreferrer" 
                     className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center hover:scale-110 transition shadow-sm">
                    <FaTwitter size={18} />
                  </a>
                  {/* LinkedIn */}
                  <a href={`https://www.linkedin.com/shareArticle?mini=true&url=${currentUrl}&title=${shareTitle}`} target="_blank" rel="noreferrer" 
                     className="w-10 h-10 rounded-full bg-blue-700 text-white flex items-center justify-center hover:scale-110 transition shadow-sm">
                    <FaLinkedinIn size={18} />
                  </a>
                </div>
              </div>
            </div>
            
          </div>

          {/* ======================================= */}
          {/* RIGHT SIDE: SIDEBAR (Categories & Popular) */}
          {/* ======================================= */}
          <div className="lg:w-1/3 space-y-6">
            
            {/* Search Box */}
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h3 className="font-bold text-gray-800 mb-3 pb-2 border-b border-gray-100">Search Posts</h3>
              <div className="flex gap-2">
                <input type="text" placeholder="Search..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
            </div>

            {/* Popular Posts */}
            <div className="bg-white rounded-xl shadow-sm p-5">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
                <TrendingUp size={18} className="text-green-600" />
                <h3 className="font-bold text-gray-800 text-lg">Popular Posts</h3>
              </div>
              <div className="space-y-4">
                {popularPosts.length > 0 ? popularPosts.map((p) => (
                  // Link pe click karne par usi component me id change ho jayegi aur naya data fetch hoga
                  <Link key={p.id} to={`/blog/${p.id}`} className="flex gap-3 group items-center">
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                      <img src={p.thumbnail_url || "https://placehold.co/100x100"} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-800 group-hover:text-green-600 text-sm line-clamp-2">{p.title}</h4>
                      <p className="text-xs text-gray-400 mt-1">{new Date(p.created_at).toLocaleDateString()}</p>
                    </div>
                  </Link>
                )) : <p className="text-sm text-gray-500">No posts available.</p>}
              </div>
            </div>

            {/* Categories */}
            <div className="bg-white rounded-xl shadow-sm p-5">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
                <Tag size={18} className="text-green-600" />
                <h3 className="font-bold text-gray-800 text-lg">Categories</h3>
              </div>
              <div className="space-y-1">
                {categories.map(category => (
                  <Link key={category} to="/blogs" className="w-full flex justify-between items-center px-3 py-2 rounded-lg text-sm transition text-gray-600 hover:bg-green-50 hover:text-green-600">
                    <span>{category}</span>
                    <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{getCategoryCount(category)}</span>
                  </Link>
                ))}
              </div>
            </div>

          </div>

        </div>
      </div>
    </div>
  );
};

export default BlogDetail;